import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  IconCamera,
  IconCar,
  IconCheck,
  IconChevronRight,
  IconFiles,
  IconFirstAidKit,
  IconId,
  IconLicense,
  IconSteeringWheel,
  IconUser,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { RIDES_VERIFICATION_STATUS, RIDES_VERIFICATION_TYPE, type RidesDriverProfileView, type RidesVerificationView } from '@actschurch/ccms-sdk';
import { useDriverProfile, useMyVerifications, findVerification } from '@/lib/me';
import { useMyPhoto } from '@/lib/photo';
import { docsUsable, splitMissing, typesFor, useMyDocuments, levelFor, currentUpload, isLiveUpload } from '@/lib/documents';
import { declarationAcceptedFor, emergencyComplete, experienceOk } from '@/lib/driverStandards';
import { documentRules, driverStandards, photoRules, useCopy } from '@/lib/settings';
import { ruleCodeCopy } from '@/lib/copy';
import { StatusPill, type StatusTone } from '@/components/ui/StatusPill';
import '@/styles/flows.css';

/** done = nothing to do, todo = the driver must act, fix = something failed, waiting = the church is checking, optional = nice to have. */
export type ReadyState = 'done' | 'todo' | 'fix' | 'waiting' | 'optional';

export interface ReadyItem {
  key: 'suspended' | 'you' | 'photo' | 'car' | 'licence' | 'contact' | 'promise' | 'id' | 'docs' | 'other';
  title: string;
  detail: string;
  state: ReadyState;
  /** Where the driver fixes it (null = nothing to tap). */
  to: string | null;
  icon: ReactNode;
}

export interface DriverReadiness {
  loading: boolean;
  error: unknown;
  /** No driver profile yet (first visit to Drive). */
  isNew: boolean;
  driver: RidesDriverProfileView | null | undefined;
  items: ReadyItem[];
  /** ready = riders can book; waiting = only church checks left; todo = the driver must do something; new = nothing set up. */
  overall: 'new' | 'todo' | 'waiting' | 'ready';
  /** Items the driver must act on (todo + fix), in order. */
  todos: ReadyItem[];
}

const PILL: Record<ReadyState, { tone: StatusTone; label: string }> = {
  done: { tone: 'ok', label: 'Done' },
  todo: { tone: 'warn', label: 'To do' },
  fix: { tone: 'bad', label: 'Needs fixing' },
  waiting: { tone: 'info', label: 'Being checked' },
  optional: { tone: 'neutral', label: 'Optional' },
};

function checkState(v: RidesVerificationView | null | undefined): { state: ReadyState; detail: string } {
  switch (v?.statusID) {
    case RIDES_VERIFICATION_STATUS.Verified:
      return { state: 'done', detail: 'Checked' };
    case RIDES_VERIFICATION_STATUS.Pending:
      return { state: 'waiting', detail: 'Show it to campus security or a campus admin to finish the check.' };
    case RIDES_VERIFICATION_STATUS.Failed:
      return { state: 'fix', detail: v.failureCode ? ruleCodeCopy(v.failureCode) : "The check didn't go through. Please try again." };
    case RIDES_VERIFICATION_STATUS.Expired:
      return { state: 'fix', detail: 'This has expired. Please check a current one.' };
    case RIDES_VERIFICATION_STATUS.Revoked:
      return { state: 'fix', detail: 'This check was withdrawn. Please contact the church office.' };
    default:
      return { state: 'todo', detail: 'Not done yet' };
  }
}

/**
 * Everything a driver needs before riders can book their lift, in the order the setup asks for it.
 * Sources: the driver profile (canList, missingUploadTypeIds, declarationAccepted, isFullyVerified),
 * the verifications, the documents view, the photo and the settings. Nothing is shown as missing
 * until its data has loaded.
 */
export function useDriverReadiness(): DriverReadiness {
  const { settings } = useCopy();
  const driverQ = useDriverProfile();
  const verQ = useMyVerifications();
  const docsQ = useMyDocuments();
  const photo = useMyPhoto();
  const photos = photoRules(settings);
  const standards = driverStandards(settings);
  const docRules = documentRules(settings);

  const d = driverQ.data;
  const loading = driverQ.isLoading || verQ.isLoading || (docRules.enabled && docsQ.isLoading) || (photos.enabled && photo.isLoading);
  const error = driverQ.error ?? verQ.error ?? null;
  const isNew = !driverQ.isLoading && !driverQ.error && d === null;

  const items: ReadyItem[] = [];
  const idV = findVerification(verQ.data, RIDES_VERIFICATION_TYPE.GovernmentId);
  const licV = findVerification(verQ.data, RIDES_VERIFICATION_TYPE.DriversLicence);

  if (d?.isSuspended) {
    items.push({ key: 'suspended', title: 'Your driver profile is paused', detail: d.suspendedReason?.trim() || 'Please contact the church office.', state: 'fix', to: null, icon: <IconAlertTriangle size={22} /> });
  }

  items.push(
    d
      ? { key: 'you', title: 'About you', detail: d.sexName ? `${d.sexName}, as on your ID` : 'Done', state: 'done', to: '/drive/profile?part=you', icon: <IconUser size={22} /> }
      : { key: 'you', title: 'About you', detail: 'Your sex, as on your ID', state: 'todo', to: null, icon: <IconUser size={22} /> },
  );

  if (photos.enabled) {
    const has = photo.hasPhoto;
    items.push({
      key: 'photo',
      title: 'Profile photo',
      detail: has ? 'Added' : photos.driverRequired ? 'Riders and campus security use it to recognise you.' : 'Riders like to see who is driving.',
      state: has ? 'done' : photos.driverRequired ? 'todo' : 'optional',
      to: d ? '/drive/profile?part=you' : null,
      icon: <IconCamera size={22} />,
    });
  }

  items.push(
    d
      ? { key: 'car', title: 'Your car', detail: [`${d.vehicle.make} ${d.vehicle.model}`.trim(), d.vehicle.plate].filter(Boolean).join(', '), state: 'done', to: '/drive/profile?part=car', icon: <IconCar size={22} /> }
      : { key: 'car', title: 'Your car', detail: 'Make, colour, number plate and seats', state: 'todo', to: null, icon: <IconCar size={22} /> },
  );

  // Driver's licence: details saved, the year first issued (when asked) and the licence check.
  if (!d) {
    items.push({ key: 'licence', title: "Driver's licence", detail: 'Code, number and expiry date', state: 'todo', to: null, icon: <IconLicense size={22} /> });
  } else if (standards.minimumYearsLicensed > 0 && !experienceOk(d.licenceFirstIssuedYear, standards.minimumYearsLicensed)) {
    items.push({ key: 'licence', title: "Driver's licence", detail: d.licenceFirstIssuedYear ? `You can offer lifts from ${d.licenceFirstIssuedYear + standards.minimumYearsLicensed}. Carpool drivers need ${standards.minimumYearsLicensed} years of driving experience.` : 'Add the year your licence was first issued.', state: 'todo', to: '/drive/profile?part=licence', icon: <IconLicense size={22} /> });
  } else if (settings.requireDriverLicenceVerification === false) {
    items.push({ key: 'licence', title: "Driver's licence", detail: d.licence.licenceCode ? `Code ${d.licence.licenceCode}` : 'Saved', state: 'done', to: '/drive/profile?part=licence', icon: <IconLicense size={22} /> });
  } else {
    const c = checkState(licV);
    items.push({ key: 'licence', title: "Driver's licence", detail: c.state === 'todo' ? 'Not checked yet' : c.detail, state: c.state, to: c.state === 'done' || c.state === 'waiting' ? '/drive/profile?part=licence' : '/drive/verify?part=licence', icon: <IconLicense size={22} /> });
  }

  // ID check (may already be done as a rider). Left out when the settings don't ask drivers for one.
  if (settings.requireDriverIdVerification !== false || idV) {
    const c = checkState(idV);
    items.push({ key: 'id', title: 'ID check', detail: c.detail, state: c.state, to: c.state === 'done' ? null : d ? '/drive/verify?part=id' : null, icon: <IconId size={22} /> });
  }

  if (standards.driverEmergency !== 'notAsked') {
    const ok = !!d && emergencyComplete(d.emergencyContact);
    const required = standards.driverEmergency === 'required';
    items.push({
      key: 'contact',
      title: 'Emergency contact',
      detail: ok ? d!.emergencyContact!.name ?? 'Added' : 'Who we should call if something happens on a ride',
      state: ok ? 'done' : required ? 'todo' : 'optional',
      to: d ? '/drive/profile?part=contact' : null,
      icon: <IconFirstAidKit size={22} />,
    });
  }

  if (standards.declarationRequired) {
    const ok = !!d && declarationAcceptedFor(d, standards.declarationTextVersion);
    items.push({ key: 'promise', title: 'Driver promise', detail: ok ? 'Agreed' : 'Read and agree to the driver declaration', state: ok ? 'done' : 'todo', to: d ? '/drive/profile?part=promise' : null, icon: <IconSteeringWheel size={22} /> });
  }

  // Documents the settings ask of drivers.
  const view = docsQ.data;
  if (docRules.enabled && docsUsable(view)) {
    const types = typesFor(view, 'driver', { clearanceForDrivers: docRules.minorRidesRequireClearedDriver });
    if (types.length) {
      const required = types.filter((tp) => levelFor(tp, 'driver', { clearanceForDrivers: docRules.minorRidesRequireClearedDriver }) === 'required');
      const missingIds = d?.missingUploadTypeIds ?? view.missingForDriver ?? [];
      const split = splitMissing(view, missingIds);
      const anyUploaded = types.some((tp) => isLiveUpload(currentUpload(view, tp.uploadTypeID)));
      let state: ReadyState;
      let detail: string;
      if (split.toUpload.length) {
        state = 'todo';
        detail = `Upload: ${split.toUpload.map((tp) => tp.name).join(', ')}`;
      } else if (split.awaitingReview.length) {
        state = 'waiting';
        detail = 'The Carpool team will check them soon.';
      } else if (required.length) {
        state = 'done';
        detail = 'Uploaded';
      } else {
        state = anyUploaded ? 'done' : 'optional';
        detail = anyUploaded ? 'Uploaded' : 'You can add copies of your documents if you like.';
      }
      items.push({ key: 'docs', title: 'Documents', detail, state, to: d ? '/drive/verify?part=docs' : null, icon: <IconFiles size={22} /> });
    }
  }

  let todos = items.filter((i) => i.state === 'todo' || i.state === 'fix');
  // The API says "can't list" but nothing above explains why: point at the profile.
  if (d && d.canList === false && todos.length === 0 && !items.some((i) => i.state === 'waiting')) {
    const other: ReadyItem = { key: 'other', title: 'Something needs a look', detail: 'Please check your driver details.', state: 'todo', to: '/drive/profile?part=car', icon: <IconAlertTriangle size={22} /> };
    items.push(other);
    todos = [other];
  }
  const waiting = items.some((i) => i.state === 'waiting');
  const overall: DriverReadiness['overall'] = isNew ? 'new' : todos.length ? 'todo' : waiting || (d && !d.isFullyVerified) ? 'waiting' : 'ready';

  return { loading, error, isNew, driver: d, items, overall, todos };
}

/** One row per readiness item: icon, title, a short status line and a pill (Done, To do …). */
export function ReadinessList({ items, interactive = true }: { items: ReadyItem[]; interactive?: boolean }) {
  return (
    <ul className="rides-ready">
      {items.map((it) => {
        const pill = PILL[it.state];
        const inner = (
          <>
            <span className="ic" aria-hidden="true">{it.state === 'done' ? <IconCheck size={22} /> : it.icon}</span>
            <span className="txt">
              <b>{it.title}</b>
              <span>{it.detail}</span>
              <span className="pill"><StatusPill tone={pill.tone}>{pill.label}</StatusPill></span>
            </span>
            {interactive && it.to && <span className="end"><span className="rides-chev" aria-hidden="true"><IconChevronRight size={22} /></span></span>}
          </>
        );
        return (
          <li key={it.key} data-state={it.state}>
            {interactive && it.to ? (
              <Link to={it.to} aria-label={`${it.title}: ${pill.label}. ${it.detail}`}>{inner}</Link>
            ) : (
              <div className="row">{inner}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
